// Coffre de pièces (H3-07) : pièces réutilisables entre démarches et collectivités, avec leur
// expiration et le verdict reporté par les agents ; dépôt depuis l'appareil, ouverture par URL
// signée, retrait.
import { Bandeau, Bouton, Champ, ConfirmationIntegree, DateChamp, EtatVide, FichierChamp, Formulaire, Selection, tailleLisible } from '@cc/ui';
import { Paperclip } from 'lucide-react';
import { useState } from 'react';
import { requete } from '../../../api/client';
import { messageErreur } from '../../../api/erreurs';
import { EtatRequete } from '../../../commun/EtatRequete';
import { date } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, ouvrirDocument, useCoffre } from '../api';
import { EnTeteCitoyen, ExigeConnexion, Section, TexteStatut } from '../commun';
import { FORMATS_PIECE, TAILLE_MAX_PIECE, TYPES_PIECE_NATIONAUX, libelleTypePiece } from '../pieces';
import type { PieceCoffre } from '../types';
import styles from '../citoyen.module.css';

function FormulaireDepot({ onFini }: { onFini: () => void }) {
  const [type, setType] = useState('');
  const [titre, setTitre] = useState('');
  const [expire, setExpire] = useState('');
  const [fichiers, setFichiers] = useState<File[]>([]);
  const [locales, setLocales] = useState<{ champ: string; message: string }[]>([]);
  const depot = useAction({
    executer: () => {
      const corps = new FormData();
      corps.set('type_piece', type);
      corps.set('titre', titre.trim() || libelleTypePiece(type));
      if (expire) corps.set('expire_le', expire);
      if (fichiers[0]) corps.set('fichier', fichiers[0]);
      return requete<PieceCoffre>('/usager/pieces', { methode: 'POST', corps });
    },
    succes: 'Pièce ajoutée au coffre.',
    invalider: [[...CLES.pieces]],
    apres: onFini,
  });
  const erreurs = [...locales, ...depot.erreursChamps.map((e) => ({ champ: `coffre-${e.champ.replace(/_/g, '-')}`, message: e.message }))];
  const erreurChamp = (c: string) => erreurs.find((e) => e.champ === c)?.message ?? null;
  return (
    <Formulaire
      libelle="Ajouter une pièce au coffre"
      onSoumettre={() => {
        const l = [!type ? { champ: 'coffre-type-piece', message: 'Choisissez le type de pièce' } : null, !fichiers[0] ? { champ: 'coffre-fichier', message: 'Choisissez un fichier' } : null].filter(
          (x): x is { champ: string; message: string } => x !== null,
        );
        setLocales(l);
        if (l.length === 0) depot.lancer(undefined);
      }}
      erreurs={erreurs}
      actions={
        <Bouton type="submit" chargement={depot.enCours} libelleChargement="Envoi en cours">
          Ajouter au coffre
        </Bouton>
      }
    >
      {depot.messageErreur && depot.erreursChamps.length === 0 ? <Bandeau type="erreur">{depot.messageErreur}</Bandeau> : null}
      <Selection id="coffre-type-piece" libelle="Type de pièce" optionVide="Choisir" options={TYPES_PIECE_NATIONAUX.map((t) => ({ valeur: t.code, libelle: t.libelle }))} value={type} onChange={(e) => setType(e.target.value)} erreur={erreurChamp('coffre-type-piece')} required />
      <Champ id="coffre-titre" libelle="Titre" value={titre} onChange={(e) => setTitre(e.target.value)} facultatif aide="Par exemple « Carte nationale d'identité, recto verso »" />
      <DateChamp id="coffre-expire-le" libelle="Date d'expiration" value={expire} onChange={(e) => setExpire(e.target.value)} facultatif aide="Laissez vide si la pièce n'expire pas ; certains types ont une validité fixée (90 jours)" erreur={erreurChamp('coffre-expire-le')} />
      <FichierChamp id="coffre-fichier" libelle="Fichier (PDF, JPEG ou PNG, 10 Mo au plus)" fichiers={fichiers} onFichiers={setFichiers} multiple={false} accept={FORMATS_PIECE} tailleMax={TAILLE_MAX_PIECE} erreur={erreurChamp('coffre-fichier')} required />
    </Formulaire>
  );
}

function LignePiece({ p }: { p: PieceCoffre }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const retrait = useAction({ executer: () => requete(`/usager/pieces/${p.id}`, { methode: 'DELETE' }), succes: 'Pièce retirée du coffre.', invalider: [[...CLES.pieces]] });
  const etat = p.expiree ? { libelle: 'Expirée', ton: 'attention' as const } : p.jugee_conforme_le ? { libelle: 'Jugée conforme', ton: 'ok' as const } : { libelle: 'Pas encore examinée', ton: 'neutre' as const };
  return (
    <li className={styles.carte}>
      <div className={styles.ligneTexte}>
        <span className={styles.ligneTitre}>{p.titre}</span>
        <span className={styles.ligneSous}>
          {libelleTypePiece(p.type_piece)} · {tailleLisible(p.taille)} · ajoutée le {date(p.cree_le)}
          {p.expire_le ? ` · ${p.expiree ? 'expirée le' : 'expire le'} ${date(p.expire_le)}` : ''}
        </span>
        <TexteStatut libelle={etat.libelle} ton={etat.ton} />
      </div>
      {erreur ? <Bandeau type="erreur">{erreur}</Bandeau> : null}
      <div className={styles.ligneBoutons}>
        <Bouton variante="secondaire" petit onClick={() => ouvrirDocument(`/usager/pieces/${p.id}/contenu`).catch((e: unknown) => setErreur(messageErreur(e)))}>
          Ouvrir
        </Bouton>
        <ConfirmationIntegree libelle="Retirer" petit question="Retirer cette pièce du coffre ? Les dossiers où elle a déjà été utilisée la conservent." libelleConfirmer="Retirer la pièce" enCours={retrait.enCours} onConfirmer={() => retrait.lancer(undefined)} />
      </div>
    </li>
  );
}

export function PageCoffre() {
  const coffre = useCoffre();
  const [ajout, setAjout] = useState(false);
  return (
    <>
      <EnTeteCitoyen
        titre="Mes pièces"
        sousTitre="Votre coffre : des pièces réutilisables pour toutes vos démarches, dans toutes les collectivités. Une pièce jugée conforme par un service est proposée au dépôt suivant."
        retour="/citoyen/compte"
        libelleRetour="Mon compte"
        actions={
          !ajout ? (
            <Bouton variante="principal" onClick={() => setAjout(true)}>
              Ajouter une pièce
            </Bouton>
          ) : null
        }
      />
      <ExigeConnexion>
        {ajout ? (
          <Section titre="Nouvelle pièce">
            <FormulaireDepot onFini={() => setAjout(false)} />
            <div>
              <Bouton variante="discret" onClick={() => setAjout(false)}>
                Annuler
              </Bouton>
            </div>
          </Section>
        ) : null}
        <Section titre="Pièces du coffre">
          <EtatRequete chargement={coffre.isPending} erreur={coffre.error} onReessayer={() => void coffre.refetch()} fonction="Le coffre">
            {(coffre.data ?? []).length === 0 ? (
              <EtatVide titre="Coffre vide" icone={Paperclip}>
                <p>Ajoutez votre pièce d&apos;identité ou un justificatif de domicile : ils seront proposés à chaque démarche qui les demande.</p>
              </EtatVide>
            ) : (
              <ul className={styles.pile} aria-label="Pièces du coffre">
                {(coffre.data ?? []).map((p) => (
                  <LignePiece key={p.id} p={p} />
                ))}
              </ul>
            )}
          </EtatRequete>
        </Section>
      </ExigeConnexion>
    </>
  );
}
